// GridFeasibilityBlock — the grid connection panel inside SelectedSiteCard.
//
// Published facts first, the model after (2026-10-13 review):
//   1. The Statnett station nearest the site, from Statnett's own lists
//      (/statnett-station): reserved, queued, withdrawn and connected
//      customers, the lists' snapshot date and links to the lists.
//   2. Planned grid projects nearby from NVE's registers (PlanNett and the
//      concession register), each with its stage and a link to the case.
//      "None" only when the registers are imported and list nothing.
//   3. SEIDR's model estimates (connection realism, timeline, capacity
//      outlook) folded under one heading, so a modelled band never reads as
//      a published figure.
//
// Data shape (see /api/v1/grid-feasibility/assess):
//   area_context.nearest_substation.{name, voltage_kv, distance_km,
//                                    capacity_mw, available_mw,
//                                    reserved_mw, queue_mw}
//   area_context.{congestion_level, total_queue_mw, queue_project_count,
//                 estimated_available_mw, planned_upgrades[], ...}
//   connection_realism.{score, band, confidence, capacity_insight}
//   time_to_power.{probability_3y, probability_5y, probability_8y,
//                  estimated_years, timeline_range,
//                  reinforcement_delay_years, regulatory_delay_years,
//                  queue_delay_years}
//   capacity_release.{probability, expected_release_mw,
//                     expected_release_year, reasons[]}
//   queue_drop.{probability}
//   reinforcement_dependency.{requires_reinforcement, dependency,
//                             nearest_upgrade_year, upgrade_description}
//   time_risk.{primary_risk, risk_factors[]}
//   phased_build.{recommended, initial_mw, expansion_mw, expansion_year,
//                 rationale}
//   substations_with_capacity[]
//   key_insights[]
//   explanation.{narrative, data_gaps[]}
//   data_freshness.{queue_data, upgrade_data}
//   area_context.planned_upgrades[].{project_name, owner, stage, stage_date,
//                                    expected_year, distance_km, case_url}
//
// /statnett-station: {station, name, voltage_kv, price_area, area_plan,
//   distance_km, reserved_mw, queued_mw, withdrawn_mw, connected_mw,
//   entries[], snapshot_date, sources[]}

import React from 'react';

const BAND_COLOR = {
  very_low: '#f87171',
  low:      '#fb923c',
  possible: '#fbbf24',
  promising:'#4ade80',
  likely:   '#4ade80',
  high:     '#4ade80',
};

function bandLabel(band) {
  if (!band) return '—';
  return String(band).replace(/_/g, ' ');
}
function pct(v, digits = 0) {
  if (v == null) return '—';
  const n = Number(v) * 100;
  return `${digits ? n.toFixed(digits) : Math.round(n)}%`;
}
function years(v) {
  if (v == null) return '—';
  return `~${Number(v).toFixed(1)}y`;
}
function mw(v) {
  if (v == null) return '—';
  return `${Number(v).toFixed(v >= 100 ? 0 : 1)} MW`;
}

const LIST_LABEL = { reservation: 'Reserved', queue: 'Queue' };
const STATNETT_PLANS = 'https://www.statnett.no/planer-for-stromnettet/omradeplaner/';

function count(entries, lt) {
  const n = entries.filter(e => e.list_type === lt).length;
  return n ? ` · ${n} customer${n === 1 ? '' : 's'}` : '';
}

// What Statnett's lists say about the station nearest the site.
export function StatnettStation({ st, constraint }) {
  if (!st) return null;
  if (!st.station) {
    return (
      <div className="gfb-panel">
        <div className="gfb-panel-title">Statnett station</div>
        <div className="gfb-sub">{st.note || "Statnett's lists are not available."}</div>
      </div>
    );
  }
  const entries = st.entries || [];
  const shown = entries.filter(e => e.list_type === 'reservation' || e.list_type === 'queue');
  // How the station was found: named above the connection point by the grid
  // company (WattApp), or only the nearest one in Statnett's lists.
  const named = st.basis === 'named';
  const where = named ? `above ${st.via}` : st.via ? `nearest station (assumed) to ${st.via}` : 'nearest station (assumed)';
  const how = named
    ? `${st.via}'s grid company names this Statnett station above it in WattApp.`
    : st.named_missing && st.via
      ? `WattApp names ${st.named_missing} above ${st.via}, but Statnett's lists have no station by that name; this is the nearest one in the lists.`
      : st.via
        ? `The grid company does not say which Statnett station is above ${st.via}; this is the nearest one in Statnett's lists.`
        : null;
  return (
    <div className="gfb-panel">
      <div className="gfb-panel-title">Statnett station {where} · {st.name}</div>
      {how && <div className="gfb-sub" style={{ marginBottom: 6 }}>{how}</div>}
      <div className="gfb-grid">
        <div className="gfb-k">Distance</div>
        <div className="gfb-v">{st.distance_km != null ? `${Number(st.distance_km).toFixed(1)} km from ${st.via || 'the site'}` : '—'}</div>
        {/* No voltage row: NVE records every Statnett station at 420 kV,
            the transmission level, not the station's voltage. */}
        {st.price_area && (<>
          <div className="gfb-k">Price area</div>
          <div className="gfb-v">{st.price_area}</div>
        </>)}
        <div className="gfb-k">Reserved</div>
        <div className="gfb-v">{mw(st.reserved_mw)}<span className="gfb-muted">{count(entries, 'reservation')}</span></div>
        <div className="gfb-k">In queue</div>
        <div className="gfb-v">{mw(st.queued_mw)}<span className="gfb-muted">{count(entries, 'queue')}</span></div>
        {st.withdrawn_mw > 0 && (<>
          <div className="gfb-k">Withdrawn</div>
          <div className="gfb-v">{mw(st.withdrawn_mw)}</div>
        </>)}
        {st.connected_mw > 0 && (<>
          <div className="gfb-k">Connected</div>
          <div className="gfb-v">{mw(st.connected_mw)}</div>
        </>)}
      </div>
      {constraint && (
        <div className="gfb-constraint">
          Statnett: {constraint.rule}{constraint.since ? ` (since ${constraint.since})` : ''}
          {constraint.source_url && <> · <a href={constraint.source_url} target="_blank" rel="noopener noreferrer">source ↗</a></>}
        </div>
      )}
      {shown.length > 0 && (
        <table className="gfb-queue-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>List</th>
              <th className="gfb-num">MW</th>
              <th className="gfb-num">Wanted</th>
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, 8).map((e, i) => (
              <tr key={`${e.list_type}-${e.case_no || i}`}>
                <td>
                  <div className="gfb-queue-name">{e.end_customer || e.statnett_customer || '—'}</div>
                  <div className="gfb-queue-type">{[e.sector, e.statnett_customer && e.statnett_customer !== e.end_customer ? `via ${e.statnett_customer}` : null].filter(Boolean).join(' · ')}</div>
                </td>
                <td>{LIST_LABEL[e.list_type]}</td>
                <td className="gfb-num">{e.mw != null ? Number(e.mw).toFixed(e.mw >= 100 ? 0 : 1) : '—'}</td>
                <td className="gfb-num gfb-muted">{e.date_wanted ? e.date_wanted.slice(0, 7) : '—'}</td>
              </tr>
            ))}
            {shown.length > 8 && (
              <tr><td colSpan={4} className="gfb-more">+ {shown.length - 8} more in Statnett's lists</td></tr>
            )}
          </tbody>
        </table>
      )}
      <div className="gfb-sub">
        Statnett's lists{st.snapshot_date ? `, snapshot ${st.snapshot_date}` : ''}
        {(st.sources || []).map(src => (
          <React.Fragment key={src.url}> · <a href={src.url} target="_blank" rel="noopener noreferrer">{src.title} ↗</a></React.Fragment>
        ))}
      </div>
    </div>
  );
}

// Planned grid projects nearby, as NVE's registers list them.
export function PlannedGridProjects({ ac, importedOn }) {
  const items = ac?.planned_upgrades || [];
  const radius = ac?.planned_upgrades_radius_km ?? 50;
  return (
    <div className="gfb-panel">
      <div className="gfb-panel-title">Planned grid projects within {radius} km</div>
      {!importedOn ? (
        <div className="gfb-sub">Unknown: NVE's planning registers are not imported on this server.</div>
      ) : items.length === 0 ? (
        <div className="gfb-sub">None listed in NVE's registers.</div>
      ) : (
        <ul className="gfb-upgrades">
          {items.map(u => (
            <li key={u.project_id}>
              {u.case_url
                ? <a href={u.case_url} target="_blank" rel="noopener noreferrer">{u.project_name}</a>
                : u.project_name}
              <span className="gfb-muted">
                {' — '}{[u.owner, u.stage && `${u.stage}${u.stage_date ? ` ${u.stage_date}` : ''}`,
                         u.expected_year && `completion ${u.expected_year}`,
                         u.distance_km != null && `${u.distance_km} km`].filter(Boolean).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="gfb-sub">
        NVE PlanNett and NVE's concession register{importedOn ? `, imported ${importedOn}` : ''}.
        {' '}Statnett's area plans and grid companies' own project pages are not read;{' '}
        <a href={STATNETT_PLANS} target="_blank" rel="noopener noreferrer">Statnett's area plans ↗</a>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   status: 'idle'|'loading'|'ready'|'error',
 *   feasibility: object|null,
 *   statnett: object|null,
 *   error: string|null,
 *   retry: () => void,
 *   note?: string|null,
 *   constraint?: object|null,
 * }} props
 */
export default function GridFeasibilityBlock({ status, feasibility, statnett, error, retry, note, constraint }) {
  if (status === 'idle') return null;

  if (status === 'loading') {
    return (
      <section className="gfb">
        <div className="gfb-head">
          <span className="gfb-eyebrow">Grid connection</span>
          <span className="gfb-loading">Fetching…</span>
        </div>
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className="gfb gfb-error">
        <div className="gfb-head">
          <span className="gfb-eyebrow">Grid connection</span>
          <button type="button" className="gfb-retry" onClick={retry}>retry</button>
        </div>
        <div className="gfb-error-msg">{error || 'Grid feasibility service unavailable.'}</div>
      </section>
    );
  }

  const d   = feasibility || {};
  const ac  = d.area_context || {};
  const ttp = d.time_to_power || {};
  const cr  = d.connection_realism || {};
  const qd  = d.queue_drop || {};
  const rd  = d.reinforcement_dependency || {};
  const cap = d.capacity_release || {};
  const trisk = d.time_risk || {};
  const phased = d.phased_build || {};
  const explanation = d.explanation || {};

  const bandColor = BAND_COLOR[cr.band] || '#94a3b8';

  const hasModel = !!feasibility;

  return (
    <section className="gfb">
      <div className="gfb-head">
        <span className="gfb-eyebrow">Grid connection</span>
      </div>
      {note && <p className="gfb-note">{note}</p>}

      <StatnettStation st={statnett} constraint={constraint} />
      {hasModel && <PlannedGridProjects ac={ac} importedOn={d.data_freshness?.upgrade_data} />}

      {/* SEIDR's model, folded: nothing below is a published figure. */}
      {hasModel && (
      <details className="gfb-model">
        <summary>
          <span className="gfb-panel-title">Model estimates</span>
          <span className="gfb-band" style={{ color: bandColor, background: `${bandColor}22` }}>{bandLabel(cr.band)}</span>
          <span className="gfb-muted"> SEIDR's model from the data above, not a figure from Statnett or the grid company</span>
        </summary>

      <div className="gfb-panel">
        <div className="gfb-panel-title">Area around the connection point</div>
        <div className="gfb-grid">
          <div className="gfb-k">Congestion</div>
          <div className="gfb-v gfb-cap">{ac.congestion_level || '—'}</div>
          <div className="gfb-k">Statnett queue within {ac.queue_radius_km ?? 15} km</div>
          <div className="gfb-v">
            {ac.total_queue_mw != null
              ? `${mw(ac.total_queue_mw)} (${ac.queue_project_count ?? 0} customer${ac.queue_project_count === 1 ? '' : 's'})`
              : '—'}
          </div>
          <div className="gfb-k">Estimated available</div>
          <div className="gfb-v">{ac.estimated_available_mw != null ? mw(ac.estimated_available_mw) : '—'}</div>
          {(ac.hydrogen_projects_mw || ac.datacenter_projects_mw || ac.industrial_projects_mw) != null && (
            <>
              <div className="gfb-k">Queue by use case</div>
              <div className="gfb-v">
                {[
                  ac.datacenter_projects_mw ? `DC ${mw(ac.datacenter_projects_mw)}` : null,
                  ac.hydrogen_projects_mw   ? `H₂ ${mw(ac.hydrogen_projects_mw)}`   : null,
                  ac.industrial_projects_mw ? `Ind ${mw(ac.industrial_projects_mw)}` : null,
                ].filter(Boolean).join(' · ') || '—'}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Timeline to power — 3/5/8y probability curve + delay breakdown. */}
      <div className="gfb-panel">
        <div className="gfb-panel-title">Timeline to power</div>
        <div className="gfb-grid">
          <div className="gfb-k">Est. years</div>
          <div className="gfb-v">
            {years(ttp.estimated_years)}
            {Array.isArray(ttp.timeline_range) && ttp.timeline_range.length === 2 && (
              <span className="gfb-muted">
                {' '}(range {ttp.timeline_range[0]}–{ttp.timeline_range[1]}y)
              </span>
            )}
          </div>
          <div className="gfb-k">Probability by year</div>
          <div className="gfb-v">
            3y {pct(ttp.probability_3y)} · 5y {pct(ttp.probability_5y)} · 8y {pct(ttp.probability_8y)}
          </div>
          <div className="gfb-k">Delay attribution</div>
          <div className="gfb-v">
            {[
              ttp.reinforcement_delay_years > 0 ? `reinforcement ${Number(ttp.reinforcement_delay_years).toFixed(1)}y` : null,
              ttp.regulatory_delay_years > 0    ? `regulatory ${Number(ttp.regulatory_delay_years).toFixed(1)}y`    : null,
              ttp.queue_delay_years > 0         ? `queue ${Number(ttp.queue_delay_years).toFixed(1)}y`               : null,
            ].filter(Boolean).join(' · ') || 'none'}
          </div>
        </div>
      </div>

      {/* Capacity release — headline forward-looking MW number. */}
      <div className="gfb-panel">
        <div className="gfb-panel-title">Capacity outlook</div>
        <div className="gfb-grid">
          <div className="gfb-k">Expected release</div>
          <div className="gfb-v">
            {cap.expected_release_mw != null ? mw(cap.expected_release_mw) : '—'}
            {cap.probability != null && (
              <span className="gfb-muted"> ({pct(cap.probability)} conf.)</span>
            )}
          </div>
          <div className="gfb-k">Release year</div>
          <div className="gfb-v">{cap.expected_release_year || 'Not scheduled'}</div>
          <div className="gfb-k">Queue drop probability</div>
          <div className="gfb-v">{pct(qd.probability)}</div>
          <div className="gfb-k">Reinforcement needed</div>
          <div
            className="gfb-v"
            style={{
              color: rd.requires_reinforcement == null
                ? '#94a3b8'
                : rd.requires_reinforcement ? '#fb923c' : '#4ade80',
            }}
          >
            {rd.requires_reinforcement == null
              ? '—'
              : rd.requires_reinforcement
                ? `Yes (${Math.round((rd.dependency || 0) * 100)}% dependency)`
                : 'No'}
          </div>
          {(rd.nearest_upgrade_year || rd.upgrade_description) && (
            <>
              <div className="gfb-k">Planned upgrade</div>
              <div className="gfb-v">
                {rd.nearest_upgrade_year || '—'}
                {rd.upgrade_description && (
                  <div className="gfb-sub">{rd.upgrade_description}</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Risk headline + phased-build recommendation. */}
      {(trisk.primary_risk || phased.recommended != null) && (
        <div className="gfb-panel">
          <div className="gfb-panel-title">Strategy signals</div>
          <div className="gfb-grid">
            {trisk.primary_risk && (
              <>
                <div className="gfb-k">Primary risk</div>
                <div className="gfb-v">{trisk.primary_risk}</div>
              </>
            )}
            {phased.recommended != null && (
              <>
                <div className="gfb-k">Phased build</div>
                <div className="gfb-v">
                  {phased.recommended
                    ? `Yes${phased.initial_mw && phased.expansion_mw ? ` (${phased.initial_mw} → ${phased.expansion_mw} MW${phased.expansion_year ? ` by ${phased.expansion_year}` : ''})` : ''}`
                    : 'No'}
                  {phased.rationale && <div className="gfb-sub">{phased.rationale}</div>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Data gaps — declared by the upstream service. */}
      {Array.isArray(explanation.data_gaps) && explanation.data_gaps.length > 0 && (
        <div className="gfb-gaps">
          <span className="gfb-eyebrow">Data gaps</span>
          <ul>
            {explanation.data_gaps.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </div>
      )}

      {/* One verdict line; the model's narrative bullets go under a fold so
          four half-contradicting sentences never read as four verdicts. */}
      <div className="gfb-panel">
        <div className="gfb-panel-title">Verdict</div>
        <div className="gfb-v">
          <strong style={{ color: bandColor, textTransform: 'capitalize' }}>{bandLabel(cr.band)}</strong>
          {ttp.probability_5y != null && <span> — {pct(ttp.probability_5y)} chance of power within 5 years</span>}
          {ttp.estimated_years != null && <span>, median {years(ttp.estimated_years).replace('~', '')}</span>}
          {rd.requires_reinforcement && <span>. Driver: reinforcement needed{rd.dependency != null ? ` (${pct(rd.dependency)} dependency)` : ''}{rd.nearest_upgrade_year ? `, upgrade ${rd.nearest_upgrade_year}` : ', none planned'}</span>}
          {cr.confidence && <span className="gfb-muted"> · confidence {String(cr.confidence).toLowerCase()}</span>}
        </div>
      </div>
      {Array.isArray(d.key_insights) && d.key_insights.length > 0 && (
        <details style={{ marginTop: 6 }}>
          <summary className="gfb-muted" style={{ cursor: 'pointer', fontSize: 11 }}>Model notes ({d.key_insights.length})</summary>
          <ul className="gfb-insights">
            {d.key_insights.map((ins, i) => (
              <li key={i}>{ins}</li>
            ))}
          </ul>
        </details>
      )}

      </details>
      )}
    </section>
  );
}
